import React, { useEffect, useState, useCallback, useRef, useContext } from 'react'
import styled from 'styled-components'
import { ImageOff, PlusCircle } from 'lucide-react'
import { useTooltip } from '../../../../components/Tooltip/useTooltip'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ProductCompositionOutletType } from '../index'
import lookup from '../../../../assets/equipment/Tables/lookup.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import pencil from '../../../../assets/equipment/Tables/pencil.svg'
import ViewProductCompositionModal from '../Visualmodalproduct'
import {
  GlobalTable,
  HeaderLine,
  Th,
  Tr,
  Td,
  TdButtons,
  DivInTd,
} from '../../../../components/styles/table-style'

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

const InfoMessage = styled.div`
  padding: 60px 40px;
  text-align: center;
  color: #999;
  font-size: 16px;
  background-color: white;
  border-radius: 8px;
`

const BoardWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

const CellContainer = styled.div`
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  width: 100%;
`;

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const API_BASE_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`
const STEEL_PRODUCT_URL = `${GATEWAY_URL}/product-final/product`

const BOARD_COVER_PALETTE = ['#EDE3B9', '#D9D5EA', '#CBE9DD', '#EFD7D9', '#D0E3F2']
const BOARD_THEME_TAG_FALLBACK_PALETTE = ['#DDECF8', '#E9DDF8', '#DDF8EA', '#F8EEDD', '#F8DDE2']

interface Grade {
  id: number
  name: string
  typeId: number
  GraduationType?: {
    id: number
    name: string
  }
}

interface StockColor {
  id: number
  stockId: number
  colorId: number
  Colors: {
    id: number
    name: string
    tenantId: number | null
  }
}

interface Stock {
  id: number
  description: string
  costPerKg?: number
  costPerMm?: number
  StockColors?: StockColor[]
}

interface Material {
  id: number
  name: string
  density: number
  tenantId: number
  Stock?: Stock[]
}

interface Resource {
  id: number
  name: string
  description: string
  categoryId: number
}

interface ScriptMaterial {
  id: number
  scriptId: number
  materialId: number
  unitOfMeasurementId?: number
  UnitOfMeasurement?: { id: number; name: string; abbreviation: string }
  Material: Material
}

interface ScriptResource {
  id: number
  scriptId: number
  resourceId: number
  unitOfMeasurementId?: number
  UnitOfMeasurement?: { id: number; name: string; abbreviation: string }
  Resource: Resource
}

interface Script {
  id: number
  name: string
  productId: number
  ScriptMaterial: ScriptMaterial[]
  ScriptResource: ScriptResource[]
}

interface GradeInProduct {
  id: number
  productId: number
  gradeId: number
  Grade: Grade
}

interface Group {
  id: number
  name: string
  tenantId: number
}

interface Family {
  id: number
  name: string
  collectionId: number
  tenantId: number
}

interface ProductResponse {
  id: number
  norm: string
  name: string
  productCode: string
  collectionId: number
  familyId: number
  groupId: number
  createdAt?: string
  updatedAt?: string
  status?: boolean
  Group?: Group
  Family?: Family
  Collection?: {
    id: number
    name: string
    tenantId: number
    collectionThemes?: Array<{ Theme?: { name?: string; color?: string } }>
  }
  GradeInProduct?: GradeInProduct[]
  Script?: Script[]
}

interface ProductListItem {
  id: number
  code: string
  name: string
  family: string
  theme: string
  grades: string
  groups: string
  colors: string
  themeTags: Array<{ name: string; color?: string | null }>
  coverImageUrl: string | null
}

const normalizeHexColor = (value?: string | null) => {
  if (!value) return null
  const normalized = value.trim().startsWith('#') ? value.trim() : `#${value.trim()}`
  const isValidHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)
  return isValidHex ? normalized : null
}

const expandShortHex = (value: string) => {
  if (value.length !== 4) return value
  return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
}

const getThemeTagColors = (rawColor: string | null | undefined, index: number) => {
  const normalized = normalizeHexColor(rawColor)
  if (!normalized) {
    return {
      backgroundColor: BOARD_THEME_TAG_FALLBACK_PALETTE[index % BOARD_THEME_TAG_FALLBACK_PALETTE.length],
      textColor: '#1f3f5f',
    }
  }

  const expanded = expandShortHex(normalized)
  const red = parseInt(expanded.slice(1, 3), 16)
  const green = parseInt(expanded.slice(3, 5), 16)
  const blue = parseInt(expanded.slice(5, 7), 16)
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000

  return {
    backgroundColor: expanded,
    textColor: brightness > 160 ? '#1b1b1b' : '#ffffff',
  }
}

const estimateThemeTagWidth = (name: string) => {
  const label = String(name || '')
  return label.length * 7 + 34
}

const arrangeThemeTagsForDenseRows = (
  tags: Array<{ name: string; color?: string | null }>,
  rowWidth: number,
) => {
  if (!tags.length) return [] as Array<{ name: string; color?: string | null }>

  const prepared = tags
    .map((tag, index) => ({
      ...tag,
      _index: index,
      _width: estimateThemeTagWidth(tag.name),
    }))
    .sort((a, b) => b._width - a._width)

  const rows: Array<{ used: number; items: typeof prepared }> = []

  prepared.forEach((tag) => {
    let bestRowIndex = -1
    let smallestRemaining = Number.POSITIVE_INFINITY

    rows.forEach((row, rowIndex) => {
      const gap = row.items.length > 0 ? 8 : 0
      const projectedUsed = row.used + gap + tag._width
      const remaining = rowWidth - projectedUsed

      if (remaining >= 0 && remaining < smallestRemaining) {
        smallestRemaining = remaining
        bestRowIndex = rowIndex
      }
    })

    if (bestRowIndex === -1) {
      rows.push({
        used: tag._width,
        items: [tag],
      })
      return
    }

    const targetRow = rows[bestRowIndex]
    targetRow.used += (targetRow.items.length > 0 ? 8 : 0) + tag._width
    targetRow.items.push(tag)
  })

  return rows
    .flatMap((row) => row.items)
    .sort((a, b) => a._index - b._index)
    .sort((a, b) => {
      const aRank = rows.findIndex((row) => row.items.some((item) => item._index === a._index))
      const bRank = rows.findIndex((row) => row.items.some((item) => item._index === b._index))
      if (aRank === bRank) {
        return a._index - b._index
      }
      return aRank - bRank
    })
    .map(({ name, color }) => ({ name, color }))
}

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding-right: 10px;
`

const BoardCard = styled.div`
  background: #f7f7f7;
  border: 1px solid #efefef;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const CreateBoardCard = styled.button`
  min-height: 262px;
  border: 2px dashed #d5d5d5;
  border-radius: 16px;
  background: #f1f1f1;
  color: #8a8a8a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;

  span {
    font-size: 16px;
    font-weight: 700;
  }

  &:hover {
    border-color: #bfbfbf;
    background: #ececec;
    color: #767676;
  }
`

const BoardCoverArea = styled.div<{ $background: string }>`
  height: 140px;
  background: ${(props) => props.$background};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const BoardCoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const BoardCoverFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #7a7a7a;

  span {
    font-size: 12px;
    font-weight: 600;
  }
`

const BoardInfo = styled.div`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BoardCode = styled.span`
  color: #8d8d8d;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BoardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #101010;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BoardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`

const ThemeTagList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`

const ThemeTagBadge = styled.span<{ $background: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => props.$color};
  background: ${(props) => props.$background};
`

const ThemeTagEmpty = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  background: #e5e7eb;
`

const CompositionTable: React.FC = () => {
  const { refresh, search, setIsLoading, viewMode } =
    useOutletContext<ProductCompositionOutletType>()
  const navigate = useNavigate()

  const [products, setProducts] = useState<ProductListItem[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalProducts, setTotalProducts] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  )
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const [deleteCandidate, setDeleteCandidate] = useState<{
    id: number
    name: string
  } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)

  const { setFeedback } = useContext(FeedbackContext)

  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const { tooltipElement, handleMouseEnter, handleMouseLeave } = useTooltip({
    maxWidth: 360,
    maxHeight: 280,
    rowHeight: 22,
    padding: 16,
    verticalGap: 4,
  })

  const handleCellHover = (
    e: React.MouseEvent,
    content: string,
    key: string,
    id: number,
  ) => {
    const ref = cellRefs.current[`${key}-${id}`]
    if (ref && content) {
      const isOverflowing = ref.scrollWidth > ref.clientWidth
      const items = content.split(',').map((s) => s.trim()).filter(Boolean)
      if (isOverflowing || items.length > 1) {
        const tooltipText = items.length > 1 ? items.join('\n') : content
        handleMouseEnter(e, tooltipText)
      }
    }
  }

  const getFirstWithEllipsis = (content: string) => {
    if (!content) return '-'
    const parts = content
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return '-'
    if (parts.length === 1) return parts[0]
    return `${parts[0]}...`
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/paginated`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: 0,
          size: visibleCount,
          search: search || undefined,
        },
      })

      const data = response.data
      let productsData: ProductResponse[] = []
      if (Array.isArray(data)) {
        productsData = data
        setTotalProducts(undefined)
      } else if (data && Array.isArray(data.content)) {
        productsData = data.content
        setTotalProducts(typeof data.totalElements === 'number' ? data.totalElements : undefined)
      }

      let transformedProducts: ProductListItem[] = productsData.map(
        (product) => {
          const family = product.Family?.name || '-';
          const grades = product.GradeInProduct?.map(gp => gp.Grade?.name).join(', ') || '-';
          const groups = product.Group?.name || '-';
          const colorSet = new Set<string>()
          const pics = (product as any).ProductItemColor || []
          for (const pic of pics) {
            const cc = pic.CollectionColor || pic.collectionColor
            if (cc && cc.name) colorSet.add(cc.name)
          }
          const colors = colorSet.size > 0 ? Array.from(colorSet).join(', ') : '-'

          const themeNames = (product.Collection?.collectionThemes || [])
            .map((ct: any) => ct.Theme?.name)
            .filter(Boolean)

          const themeTags = (product.Collection?.collectionThemes || [])
            .map((ct: any) => ({
              name: ct.Theme?.name || '',
              color: ct.Theme?.color || null,
            }))
            .filter((tag: { name: string }) => Boolean(tag.name))

          const theme = themeNames.length > 0 ? themeNames.join(', ') : '-'

          return {
            id: product.id,
            code: product.productCode || product.norm || '-',
            name: product.name || '-',
            family,
            theme,
            grades,
            groups,
            colors,
            themeTags,
            coverImageUrl: null,
          }
        },
      )


      if (search) {
        transformedProducts = transformedProducts.filter(
          (item) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.code.toLowerCase().includes(search.toLowerCase()) ||
            item.family.toLowerCase().includes(search.toLowerCase()) ||
            item.theme.toLowerCase().includes(search.toLowerCase()),
        )
      }

      const productsWithCover = await Promise.all(
        transformedProducts.map(async (item) => {
          try {
            const coverResponse = await axios.get(`${STEEL_PRODUCT_URL}/${item.id}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })

            const coverAttachment = coverResponse.data?.coverAttachment
            const coverImageUrl =
              coverAttachment?.filePath ||
              coverAttachment?.fileUrl ||
              null

            return {
              ...item,
              coverImageUrl,
            }
          } catch (_error) {
            return item
          }
        }),
      )

      setProducts(productsWithCover)
    } catch (err: unknown) {
      console.error('Erro ao buscar produtos:', err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError('Não autorizado. Por favor, faça login novamente.')
          navigate('/login')
        } else if (err.response?.status === 404) {
          setProducts([])
        } else {
          setError('Não foi possível carregar os produtos. Tente novamente.')
        }
      } else {
        setError('Erro inesperado ao carregar produtos.')
      }
    } finally {
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }, [search, refresh, navigate, visibleCount])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleViewDetails = (productId: number) => {
    setSelectedProductId(productId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProductId(null)
  }

  const handleEdit = (productId: number) => {
    navigate(`/panel/composition-product/edit/${productId}`)
  }

  const handleDelete = (productId: number) => {
    const product = products.find((p) => p.id === productId)
    const productName = product ? product.name : productId

    setDeleteCandidate({ id: productId, name: String(productName) })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Por favor, faça login novamente.',
      })
      navigate('/login')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await axios.delete(`${API_BASE_URL}/${deleteCandidate.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      setProducts((prevProducts) =>
        prevProducts.filter((product) => product.id !== deleteCandidate.id),
      )

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Produto excluído com sucesso!',
      })
    } catch (err: unknown) {
      console.error(`Erro ao excluir produto ${deleteCandidate.id}:`, err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError(
            'Não autorizado para excluir. Por favor, faça login novamente.',
          )
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: 'Não autorizado para excluir. Por favor, faça login novamente.',
          })
          navigate('/login')
        } else {
          const errorMessage =
            err.response?.data?.message || 'Falha ao excluir o produto.'
          setError(errorMessage)
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: errorMessage,
          })
        }
      } else {
        setError('Erro inesperado ao excluir produto.')
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro inesperado ao excluir produto.',
        })
      }
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
      setDeleteCandidate(null)
    }
  }

  if (loading) {
    return <InfoMessage>Carregando produtos...</InfoMessage>
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  if (products.length === 0) {
    return (
      <InfoMessage>
        {search
          ? 'Nenhum produto encontrado com os critérios de busca.'
          : 'Nenhum produto cadastrado. Clique em "Cadastrar produto" para começar.'}
      </InfoMessage>
    )
  }

  return (
    <>
      {viewMode === 'table' ? (
        <TableWrapper>
          <GlobalTable>
            <thead>
              <HeaderLine>
                <Th>Código</Th>
                <Th>Produto</Th>
                <Th>Família</Th>
                <Th>Tema</Th>
                <Th>Grades</Th>
                <Th>Grupos</Th>
                <Th>Cores</Th>
                <Th></Th>
              </HeaderLine>
            </thead>
            <tbody>
              {products.map((product) => (
                <Tr key={product.id}>
                  <Td data-label='Código'>{product.code}</Td>
                  <Td data-label='Produto'>{product.name}</Td>
                  <Td data-label='Família'>
                    <CellContainer
                      ref={(el) =>
                        (cellRefs.current[`family-${product.id}`] = el)
                      }
                      onMouseEnter={(e: any) =>
                        handleCellHover(
                          e,
                          product.family,
                          'family',
                          product.id,
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {getFirstWithEllipsis(product.family)}
                    </CellContainer>
                  </Td>
                  <Td data-label='Tema'>
                    <CellContainer
                      ref={(el) =>
                        (cellRefs.current[`theme-${product.id}`] = el)
                      }
                      onMouseEnter={(e: any) =>
                        handleCellHover(
                          e,
                          product.theme,
                          'theme',
                          product.id,
                        )
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {getFirstWithEllipsis(product.theme)}
                    </CellContainer>
                  </Td>
                  <Td data-label='Grades'>
                    <CellContainer
                      ref={(el) =>
                        (cellRefs.current[`grades-${product.id}`] = el)
                      }
                      onMouseEnter={(e: any) =>
                        handleCellHover(e, product.grades, 'grades', product.id)
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {getFirstWithEllipsis(product.grades)}
                    </CellContainer>
                  </Td>
                  <Td data-label='Grupos'>
                    <CellContainer
                      ref={(el) =>
                        (cellRefs.current[`groups-${product.id}`] = el)
                      }
                      onMouseEnter={(e: any) =>
                        handleCellHover(e, product.groups, 'groups', product.id)
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {getFirstWithEllipsis(product.groups)}
                    </CellContainer>
                  </Td>
                  <Td data-label='Cores'>
                    <CellContainer
                      ref={(el) =>
                        (cellRefs.current[`colors-${product.id}`] = el)
                      }
                      onMouseEnter={(e: any) =>
                        handleCellHover(e, product.colors, 'colors', product.id)
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {getFirstWithEllipsis(product.colors)}
                    </CellContainer>
                  </Td>
                  <TdButtons>
                    <DivInTd className='hover-blue' onClick={() => handleViewDetails(product.id)}>
                      <img src={lookup} alt='Visualizar' title='Visualizar' />
                    </DivInTd>
                    <DivInTd className='hover-blue' onClick={() => handleEdit(product.id)}>
                      <img src={pencil} alt='Editar' title='Editar' />
                    </DivInTd>
                    <DivInTd className='hover-red' onClick={() => handleDelete(product.id)}>
                      <img src={trash} alt='Excluir' title='Excluir' />
                    </DivInTd>
                  </TdButtons>
                </Tr>
              ))}
            </tbody>
          </GlobalTable>
          <LoadMoreButton
            currentCount={products.length}
            totalCount={totalProducts}
            hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
            onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          />
        </TableWrapper>
      ) : (
        <BoardWrapper>
          <BoardGrid>
            {products.map((product, index) => (
              <BoardCard key={product.id}>
                <BoardCoverArea
                  $background={BOARD_COVER_PALETTE[index % BOARD_COVER_PALETTE.length]}
                  onClick={() => handleViewDetails(product.id)}
                >
                  {product.coverImageUrl ? (
                    <BoardCoverImage
                      src={product.coverImageUrl}
                      alt={product.name}
                    />
                  ) : (
                    <BoardCoverFallback>
                      <ImageOff size={34} />
                      <span>Sem capa</span>
                    </BoardCoverFallback>
                  )}
                </BoardCoverArea>

                <BoardInfo>
                  <BoardCode>
                    {`Cod. ${product.code}`}
                  </BoardCode>
                  <BoardTitle title={product.name}>{product.name}</BoardTitle>

                  <BoardFooter>
                    <ThemeTagList>
                      {product.themeTags.length > 0 ? (
                        arrangeThemeTagsForDenseRows(product.themeTags, 286).map((themeTag, themeIndex) => {
                          const { backgroundColor, textColor } = getThemeTagColors(
                            themeTag.color,
                            themeIndex,
                          )

                          return (
                            <ThemeTagBadge
                              key={`${product.id}-${themeTag.name}-${themeIndex}`}
                              $background={backgroundColor}
                              $color={textColor}
                            >
                              {themeTag.name}
                            </ThemeTagBadge>
                          )
                        })
                      ) : (
                        <ThemeTagEmpty>Sem tema</ThemeTagEmpty>
                      )}
                    </ThemeTagList>
                  </BoardFooter>

                </BoardInfo>
              </BoardCard>
            ))}

            <CreateBoardCard onClick={() => navigate('/panel/composition-product/create')}>
              <PlusCircle size={28} />
              <span>Nova Ficha</span>
            </CreateBoardCard>
          </BoardGrid>

          <LoadMoreButton
            currentCount={products.length}
            totalCount={totalProducts}
            hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
            onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          />
        </BoardWrapper>
      )}

      {tooltipElement}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Tem certeza que deseja excluir este produto?"
        message={
          deleteCandidate
            ? `Produto: ${deleteCandidate.name}
              Essa ação não poderá ser desfeita.`
            : 'Essa ação não poderá ser desfeita.'
        }
        actions={[
          {
            label: 'Não, voltar',
            onClick: () => setShowDeleteConfirm(false),
            variant: 'outline',
          },
          {
            label: 'Sim, excluir',
            onClick: handleConfirmDelete,
            variant: 'danger',
          },
        ]}
      />

      {isModalOpen && selectedProductId && (
        <ViewProductCompositionModal
          productId={selectedProductId}
          onClose={handleCloseModal}
        />
      )}
    </>
  )
}

export default CompositionTable